import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { API } from "../../../../API";
import useOpen from "../../../../hooks/useOpen";
import { CaregiverMobile, CaregiverUpdateParams } from "../../../../messages";
import { STRINGS } from "../../../../strings";
import { translate } from "../../../../translate";
import { AppButton } from "../../../Generic/AppButton";
import AppModal from "../../../Generic/AppModal/AppModal";
import { If } from "../../../Generic/If";
import Spinner from "../../../Generic/Spinner";
import Question from "../../../OnboardingSection/Question";
import useModal from "../../../useModal";
import AddressDialog from "../AddressDialog";
import AuthForm, { AuthFormTitle } from "../AuthForm";

const schema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  address: z.string().min(1),
  addressGeoLocation: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
});

type FormSchemaData = z.infer<typeof schema>;

interface Props {
  api: API;
  caregiver: CaregiverMobile;
  onValid: (params: { api: API; caregiver: CaregiverMobile }) => void;
}

const ConfirmInformationStage = (props: Props) => {
  const addressDialog = useOpen();
  const modal = useModal();

  const { register, setValue, handleSubmit } = useForm<FormSchemaData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: props.caregiver.firstName,
      lastName: props.caregiver.lastName,
      address: props.caregiver.address,
      addressGeoLocation: props.caregiver.addressGeoLocation,
    },
  });

  const handleClickPlace = (place: google.maps.places.PlaceResult) => {
    if (place.geometry === undefined || place.formatted_address === undefined) {
      return modal.setModalAsError({
        title: translate(STRINGS.ERROR),
        message: translate(STRINGS.SOMETHING_WENT_WRONG),
      });
    }
    setValue("addressGeoLocation", {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    });

    setValue("address", place.formatted_address);

    addressDialog.actions.dismiss();
  };

  const patchDetailsMutation = useMutation(
    (params: CaregiverUpdateParams) => props.api.caregiverUpdateProfile(params),
    {
      onSuccess: () => {
        return props.onValid({ api: props.api, caregiver: props.caregiver });
      },
    }
  );

  const onSubmit = handleSubmit((data) => patchDetailsMutation.mutate(data));

  return (
    <ConfirmInformationStage.Styled onSubmit={onSubmit}>
      <AuthFormTitle>{translate(STRINGS.PLEASE_CONFIRM_YOUR_INFORMATION)}:</AuthFormTitle>

      <Question.Input {...register("firstName")} placeholder={translate(STRINGS.FIRST_NAME)} />

      <Question.Input {...register("lastName")} placeholder={translate(STRINGS.LAST_NAME)} />

      <Question.Input
        {...register("address")}
        placeholder={translate(STRINGS.ADDRESS)}
        onClick={addressDialog.actions.open}
        onFocus={addressDialog.actions.open}
      />

      <AppButton type="submit">
        <If condition={patchDetailsMutation.isLoading}>
          <span>
            <Spinner size={24} />
          </span>
        </If>
        <span>{translate(STRINGS.NEXT)}</span>
      </AppButton>

      <AddressDialog
        isOpen={addressDialog.isOpen}
        defaultValue={props.caregiver.address}
        onClickPlace={handleClickPlace}
        onRequestClose={addressDialog.actions.dismiss}
      />

      <AppModal {...modal.modalProps} />
    </ConfirmInformationStage.Styled>
  );
};

ConfirmInformationStage.Styled = styled(AuthForm)``;

export default ConfirmInformationStage;
